/**
 * Campaign detail Dialog: range KPIs, daily metrics table, TikTok deep link,
 * and in-popup adgroup → ads drill-down.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from "react"

import {
  useAdgroupAds,
  useCampaignAdgroups,
  useCampaignDailyMetrics,
  useCampaignLandingUrl,
  type AdGroupRow,
  type CampaignDailyTotals,
  type LiveAdRow,
} from "@/api/hooks/adsManager"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { clampIsoRangeToMaxDays, labelFromIsoRange } from "@/lib/dates"

import {
  computePresetRange,
  ROOTS_REPORTING_TIMEZONE,
  type AdsManagerDatePreset,
} from "../lib/datePresets"
import {
  formatCellValue,
  formatCurrency,
  formatRoi,
} from "../lib/formatters"
import {
  funnelCvrTextClass,
  funnelCvrTitle,
  getFunnelCvrBand,
} from "../lib/funnelCvr"
import { DateRangeSelector } from "./DateRangeSelector"
import { StatusBadge } from "./StatusBadge"

interface CampaignDetailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaignId: string
  campaignName: string
  advertiserId?: string
  /** Initial range from the Ads Manager page. */
  startDate: string
  endDate: string
  useReportingTimezone?: boolean
}

const DAILY_COLUMNS = [
  { id: "date", label: "Date" },
  { id: "cost", label: "Spend" },
  { id: "revenue", label: "Revenue" },
  { id: "profit", label: "Profit" },
  { id: "roi", label: "ROI" },
  { id: "cpa", label: "CPA" },
  { id: "rpc", label: "RPC" },
  { id: "ctr", label: "CTR" },
  { id: "cvr", label: "CVR" },
  { id: "p1_cvr", label: "p1CVR" },
  { id: "p2_cvr", label: "p2CVR" },
  { id: "gclicks", label: "gClick" },
  { id: "clicks", label: "Click" },
] as const

/** Detail modal custom ranges cannot exceed this inclusive day count. */
const DETAIL_MAX_RANGE_DAYS = 30

function buildTikTokManageLink(
  advertiserId: string,
  startDate?: string,
  endDate?: string,
): string {
  const params = new URLSearchParams()
  params.set("aadvid", String(advertiserId))
  if (startDate) params.set("st", startDate)
  if (endDate) params.set("et", endDate)
  return `https://ads.tiktok.com/i18n/manage/campaign?${params.toString()}`
}

function pctDelta(current: number, prior: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(prior)) return null
  if (prior === 0) return current === 0 ? 0 : null
  return ((current - prior) / Math.abs(prior)) * 100
}

function formatDelta(delta: number | null): string {
  if (delta == null || !Number.isFinite(delta)) return "—"
  const sign = delta > 0 ? "+" : ""
  return `${sign}${delta.toFixed(1)}%`
}

function DeltaChip({
  label,
  current,
  prior,
  format = "currency",
}: {
  label: string
  current: number | null | undefined
  prior: number | null | undefined
  format?: "currency" | "roi" | "number" | "percent"
}) {
  const cur = Number(current ?? 0)
  const prev = Number(prior ?? 0)
  const delta = pctDelta(cur, prev)
  const value =
    format === "currency"
      ? formatCurrency(cur)
      : format === "roi"
        ? formatRoi(current ?? null)
        : format === "percent"
          ? formatCellValue("ctr", current ?? null)
          : String(Math.round(cur))
  const tone =
    delta == null
      ? "text-white/50"
      : delta > 0
        ? "text-green-400"
        : delta < 0
          ? "text-red-400"
          : "text-white/50"
  return (
    <div className="rounded-md border border-[#4a4a4a] bg-[#262626] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-white/50">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold text-white">{value}</div>
      <div className={`mt-0.5 text-[10px] ${tone}`}>
        vs prior {formatDelta(delta)}
      </div>
    </div>
  )
}

function AdgroupAdsList({ adgroupId }: { adgroupId: string }) {
  const { data, isLoading, isError, error } = useAdgroupAds(adgroupId, {
    enabled: true,
  })
  const ads: LiveAdRow[] = data?.data ?? []

  if (isLoading) {
    return <p className="px-3 py-2 text-[11px] text-white/50">Loading ads…</p>
  }
  if (isError) {
    return (
      <p className="px-3 py-2 text-[11px] text-red-400">
        {(error as Error)?.message || "Failed to load ads"}
      </p>
    )
  }
  if (ads.length === 0) {
    return (
      <p className="px-3 py-2 text-[11px] text-white/50">No ads returned.</p>
    )
  }
  return (
    <ul className="space-y-1 px-3 py-2">
      {ads.map((ad) => (
        <li
          key={ad.ad_id}
          className="flex items-center gap-2 rounded border border-[#3a3a3a] bg-[#1a1a1a] px-2 py-1.5"
        >
          {ad.thumbnail_url ? (
            <img
              src={ad.thumbnail_url}
              alt=""
              className="h-10 w-8 shrink-0 rounded object-cover"
            />
          ) : (
            <div className="flex h-10 w-8 shrink-0 items-center justify-center rounded bg-[#333] text-[9px] text-white/40">
              —
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs text-white" title={ad.ad_name}>
              {ad.ad_name || ad.ad_id}
            </div>
            <div className="text-[10px] text-white/45">
              {ad.media_type}
              {ad.is_smart_plus ? " · Smart+" : ""}
            </div>
          </div>
          {ad.status ? <StatusBadge status={ad.status} /> : null}
        </li>
      ))}
    </ul>
  )
}

/** Adgroups → ads for a single calendar day (nested under a daily row). */
function DayStructure({
  campaignId,
  day,
  enabled,
}: {
  campaignId: string
  day: string
  enabled: boolean
}) {
  const { data, isLoading, isError, error } = useCampaignAdgroups(
    campaignId,
    day,
    day,
    { enabled },
  )
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set())
  const rows: AdGroupRow[] = data?.data ?? []

  const toggle = useCallback((id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  useEffect(() => {
    setOpenIds(new Set())
  }, [campaignId, day])

  if (isLoading) {
    return <p className="px-3 py-2 text-[11px] text-white/50">Loading adgroups…</p>
  }
  if (isError) {
    return (
      <p className="px-3 py-2 text-[11px] text-red-400">
        {(error as Error)?.message || "Failed to load adgroups"}
      </p>
    )
  }
  if (rows.length === 0) {
    return (
      <p className="px-3 py-2 text-[11px] text-white/50">
        No adgroups with spend on this day.
      </p>
    )
  }

  /** Metric cells that line up under the parent daily columns. */
  const metricCell = (id: string, value: unknown) => (
    <span className="whitespace-nowrap text-[11px] text-white/80">
      {formatCellValue(id, value)}
    </span>
  )

  return (
    <div className="overflow-hidden rounded border border-[#3a3a3a]">
      <div className="grid grid-cols-[minmax(180px,2fr)_repeat(5,minmax(64px,1fr))] gap-2 border-b border-[#3a3a3a] bg-[#1a1a1a] px-3 py-1 text-[10px] uppercase tracking-wide text-white/40">
        <span>Ad group</span>
        <span className="text-right">Spend</span>
        <span className="text-right">CPA</span>
        <span className="text-right">CTR</span>
        <span className="text-right">CVR</span>
        <span className="text-right">Click</span>
      </div>
      {rows.map((ag) => {
        const open = openIds.has(ag.adgroup_id)
        return (
          <div
            key={ag.adgroup_id}
            className="border-b border-[#3a3a3a] last:border-b-0"
          >
            <button
              type="button"
              onClick={() => toggle(ag.adgroup_id)}
              className="grid w-full grid-cols-[minmax(180px,2fr)_repeat(5,minmax(64px,1fr))] items-center gap-2 bg-[#222] px-3 py-2 text-left hover:bg-[#2a2a2a]"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 text-[10px] text-white/55">
                  {open ? "∧" : "∨"}
                </span>
                <span className="min-w-0 truncate text-xs text-white">
                  {ag.adgroup_name}
                </span>
                <StatusBadge status={String(ag.status ?? "")} />
              </span>
              <span className="text-right">{metricCell("cost", ag.cost)}</span>
              <span className="text-right">{metricCell("cpa", ag.cpa)}</span>
              <span className="text-right">{metricCell("ctr", ag.ctr)}</span>
              <span className="text-right">
                {metricCell("cvr", ag.cvr ?? null)}
              </span>
              <span className="text-right">
                {metricCell("clicks", ag.clicks)}
              </span>
            </button>
            {open ? <AdgroupAdsList adgroupId={ag.adgroup_id} /> : null}
          </div>
        )
      })}
    </div>
  )
}

export function CampaignDetailModal({
  open,
  onOpenChange,
  campaignId,
  campaignName,
  advertiserId: advertiserIdProp,
  startDate: initialStart,
  endDate: initialEnd,
  useReportingTimezone = true,
}: CampaignDetailModalProps) {
  const [preset, setPreset] = useState<AdsManagerDatePreset>("last7")
  const [startDate, setStartDate] = useState(initialStart)
  const [endDate, setEndDate] = useState(initialEnd)
  const [displayLabel, setDisplayLabel] = useState(
    () => labelFromIsoRange(initialStart, initialEnd),
  )
  /** Expanded daily row — shows adgroups/ads for that calendar day. */
  const [expandedDay, setExpandedDay] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    // Always open on last 7 reporting days (rolling).
    const range = computePresetRange(
      "last7",
      useReportingTimezone ? "reporting" : "local",
      ROOTS_REPORTING_TIMEZONE,
    )
    setPreset("last7")
    setStartDate(range.startDate)
    setEndDate(range.endDate)
    setDisplayLabel(range.displayLabel)
    setExpandedDay(null)
  }, [open, campaignId, useReportingTimezone])

  useEffect(() => {
    setExpandedDay(null)
  }, [startDate, endDate])

  const { data, isLoading, isError, error } = useCampaignDailyMetrics(
    campaignId,
    startDate,
    endDate,
    { enabled: open && Boolean(campaignId) },
  )

  const landingQuery = useCampaignLandingUrl(campaignId, {
    enabled: open && Boolean(campaignId),
  })
  const landingUrl = landingQuery.data?.landing_page_url || null

  const campaign = data?.campaign
  const advertiserId = campaign?.advertiser_id || advertiserIdProp || ""
  const totals = data?.totals
  const prior = data?.prior_totals
  const rows = data?.data ?? []

  const tiktokUrl = useMemo(() => {
    if (!advertiserId) return null
    return buildTikTokManageLink(advertiserId, startDate, endDate)
  }, [advertiserId, startDate, endDate])

  const handlePresetChange = (
    next: AdsManagerDatePreset,
    nextStart: string,
    nextEnd: string,
    nextLabel: string,
  ) => {
    setPreset(next)
    setStartDate(nextStart)
    setEndDate(nextEnd)
    setDisplayLabel(nextLabel)
  }

  const handleCustomRangeApply = (
    nextStart: string,
    nextEnd: string,
    _nextLabel: string,
  ) => {
    const clamped = clampIsoRangeToMaxDays(
      nextStart,
      nextEnd,
      DETAIL_MAX_RANGE_DAYS,
    )
    setPreset("custom")
    setStartDate(clamped.startDate)
    setEndDate(clamped.endDate)
    setDisplayLabel(labelFromIsoRange(clamped.startDate, clamped.endDate))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[min(1100px,96vw)] max-w-[1100px] flex-col gap-0 overflow-hidden border-[#4a4a4a] bg-[#2d2d2d] p-0 text-white">
        {/* pr-12 clears the absolute Dialog close (right-4 top-4) so URL /
            TikTok actions don't sit under the X hit target. */}
        <DialogHeader className="shrink-0 space-y-2 border-b border-[#3f3f3f] px-5 py-4 pr-12">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-1 pr-2">
              <DialogTitle className="truncate text-base font-semibold text-white">
                {campaign?.campaign_name || campaignName}
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/60">
                {campaign?.status ? (
                  <StatusBadge status={campaign.status} />
                ) : null}
                <span className="truncate">
                  Adv {advertiserId || "—"}
                  {campaign?.advertiser_name
                    ? ` · ${campaign.advertiser_name}`
                    : ""}
                </span>
                {campaign?.objective_type ? (
                  <span>· {campaign.objective_type}</span>
                ) : null}
                {(campaign?.budget || campaign?.bid) ? (
                  <span>
                    · Budget {formatCurrency(campaign.budget)} · Bid{" "}
                    {formatCurrency(campaign.bid)}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {landingQuery.isLoading ? (
                <span className="rounded-md border border-[#555] bg-[#333] px-3 py-1.5 text-xs text-white/50">
                  Campaign URL…
                </span>
              ) : landingUrl ? (
                <a
                  href={landingUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={landingUrl}
                  className="rounded-md border border-[#5a8a6a]/50 bg-[#2d4a38] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#365a44]"
                >
                  Campaign&apos;s URL ↗
                </a>
              ) : (
                <span
                  className="rounded-md border border-[#555] bg-[#333] px-3 py-1.5 text-xs text-white/40"
                  title={
                    landingQuery.data?.message ||
                    "No landing page found on TikTok ads"
                  }
                >
                  Campaign&apos;s URL
                </span>
              )}
              {tiktokUrl ? (
                <a
                  href={tiktokUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-md border border-[#8fa0ef]/45 bg-[#3a4060] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#4a5380]"
                >
                  Open in TikTok ↗
                </a>
              ) : null}
            </div>
          </div>

          <div className="pt-1">
            <DateRangeSelector
              datePreset={preset}
              startDate={startDate}
              endDate={endDate}
              displayLabel={displayLabel}
              onPresetChange={handlePresetChange}
              onCustomRangeApply={handleCustomRangeApply}
              useReportingTimezone={useReportingTimezone}
              reportingTimeZone={ROOTS_REPORTING_TIMEZONE}
              maxRangeDays={DETAIL_MAX_RANGE_DAYS}
            />
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="text-sm text-white/60">Loading daily metrics…</p>
          ) : isError ? (
            <p className="text-sm text-red-400">
              {(error as Error)?.message || "Failed to load campaign detail"}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-10">
                <DeltaChip
                  label="Spend"
                  current={totals?.cost}
                  prior={prior?.cost}
                />
                <DeltaChip
                  label="Revenue"
                  current={totals?.revenue}
                  prior={prior?.revenue}
                />
                <DeltaChip
                  label="Profit"
                  current={totals?.profit}
                  prior={prior?.profit}
                />
                <DeltaChip
                  label="ROI"
                  current={totals?.roi}
                  prior={prior?.roi}
                  format="roi"
                />
                <DeltaChip
                  label="CPA"
                  current={totals?.cpa}
                  prior={prior?.cpa}
                />
                <DeltaChip
                  label="RPC"
                  current={totals?.rpc}
                  prior={prior?.rpc}
                />
                <DeltaChip
                  label="CTR"
                  current={totals?.ctr}
                  prior={prior?.ctr}
                  format="percent"
                />
                <DeltaChip
                  label="CVR"
                  current={totals?.cvr}
                  prior={prior?.cvr}
                  format="percent"
                />
                <DeltaChip
                  label="gClick"
                  current={totals?.gclicks}
                  prior={prior?.gclicks}
                  format="number"
                />
                <DeltaChip
                  label="Click"
                  current={totals?.clicks}
                  prior={prior?.clicks}
                  format="number"
                />
              </div>

              {(data?.best_day || data?.worst_day) && (
                <div className="flex flex-wrap gap-3 text-[11px] text-white/70">
                  {data.best_day ? (
                    <span>
                      Best day{" "}
                      <strong className="text-green-400">
                        {data.best_day.date}
                      </strong>{" "}
                      ({formatCurrency(data.best_day.profit)} profit)
                    </span>
                  ) : null}
                  {data.worst_day ? (
                    <span>
                      Worst day{" "}
                      <strong className="text-red-400">
                        {data.worst_day.date}
                      </strong>{" "}
                      ({formatCurrency(data.worst_day.profit)} profit)
                    </span>
                  ) : null}
                </div>
              )}

              <section className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-white/60">
                  Daily breakdown
                </h3>
                <p className="text-[11px] text-white/45">
                  Click a date to expand ad groups &amp; ads for that day.
                </p>
                <div className="max-h-[420px] overflow-auto rounded-md border border-[#4a4a4a]">
                  <table className="w-full min-w-[880px] border-collapse text-xs">
                    <thead className="sticky top-0 z-10 bg-[#333]">
                      <tr>
                        {DAILY_COLUMNS.map((col) => (
                          <th
                            key={col.id}
                            className={`whitespace-nowrap px-2 py-1.5 font-medium text-white/70 ${
                              col.id === "date" ? "text-left" : "text-right"
                            }`}
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const isOpen = expandedDay === row.date
                        return (
                          <Fragment key={row.date}>
                            <tr
                              className={`cursor-pointer border-t border-[#3a3a3a] hover:bg-[#333]/60 ${
                                isOpen ? "bg-[#333]/80" : ""
                              }`}
                              onClick={() =>
                                setExpandedDay(isOpen ? null : row.date)
                              }
                            >
                              {DAILY_COLUMNS.map((col) => {
                                const raw =
                                  col.id === "date"
                                    ? row.date
                                    : row[col.id as keyof typeof row]
                                const funnelBand = getFunnelCvrBand(
                                  col.id,
                                  raw,
                                )
                                const funnelClass =
                                  funnelCvrTextClass(funnelBand)
                                return (
                                  <td
                                    key={col.id}
                                    title={funnelCvrTitle(col.id, funnelBand)}
                                    className={`whitespace-nowrap px-2 py-1 ${
                                      col.id === "date"
                                        ? "text-left text-white/80"
                                        : `text-right ${funnelClass || "text-white"}`
                                    }`}
                                  >
                                    {col.id === "date" ? (
                                      <span className="inline-flex items-center gap-1.5">
                                        <span className="text-[10px] text-white/50">
                                          {isOpen ? "∧" : "∨"}
                                        </span>
                                        {String(raw)}
                                      </span>
                                    ) : (
                                      formatCellValue(col.id, raw)
                                    )}
                                  </td>
                                )
                              })}
                            </tr>
                            {isOpen ? (
                              <tr className="border-t border-[#3a3a3a] bg-[#1c1c1c]">
                                <td
                                  colSpan={DAILY_COLUMNS.length}
                                  className="px-3 py-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="mb-1.5 text-[10px] uppercase tracking-wide text-white/45">
                                    Ad groups · {row.date}
                                  </div>
                                  <DayStructure
                                    campaignId={campaignId}
                                    day={row.date}
                                    enabled={open && Boolean(campaignId)}
                                  />
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        )
                      })}
                    </tbody>
                    {totals ? (
                      <tfoot>
                        <tr className="border-t border-[#555] bg-[#2a2a2a] font-semibold">
                          <td className="px-2 py-1.5 text-left text-white/80">
                            Total
                          </td>
                          {DAILY_COLUMNS.slice(1).map((col) => {
                            const raw =
                              totals[col.id as keyof CampaignDailyTotals]
                            const funnelBand = getFunnelCvrBand(col.id, raw)
                            const funnelClass = funnelCvrTextClass(funnelBand)
                            return (
                              <td
                                key={col.id}
                                title={funnelCvrTitle(col.id, funnelBand)}
                                className={`whitespace-nowrap px-2 py-1.5 text-right ${
                                  funnelClass || "text-white"
                                }`}
                              >
                                {formatCellValue(col.id, raw)}
                              </td>
                            )
                          })}
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
